import { z } from 'zod';
import { CREW_TASKS, INCIDENT_KINDS, MATERIAL_FLOWS, PHOTO_KINDS, REPORT_PHOTO_KINDS, } from './enums.js';
import { BASIS_POINTS } from './units.js';
/**
 * Сменный отчёт.
 *
 * Отчёт привязан к паре «смена + агрегат»: выработка считается на конкретного
 * человека и конкретный пресс. Если на агрегате работали вдвоём (смешивание),
 * доли участия делят выработку между ними.
 *
 * Всё, что вводит оператор, приезжает одним событием SHIFT_REPORT_SUBMITTED.
 * Исправление — отдельное событие SHIFT_REPORT_CORRECTED с обязательной причиной;
 * исходный отчёт остаётся в истории.
 */
export const participantSchema = z.object({
    userId: z.string().uuid(),
    /** Доля участия в базисных пунктах. Один человек — 10000, двое поровну — по 5000. */
    shareBp: z.number().int().positive().max(BASIS_POINTS),
});
export const reportOutputSchema = z.object({
    productSizeId: z.string().uuid(),
    /** Годная плитка, штук. Именно она попадает на склад готовой продукции. */
    qtyPieces: z.number().int().min(0),
    /**
     * Брак, штук. На склад не попадает: его переплавляют тут же, на линии.
     * Отгружать и выбрасывать брак нельзя, поэтому «куда делся» — вопрос
     * не учёта, а нормы потерь при переплавке.
     */
    defectPieces: z.number().int().min(0).default(0),
});
export const reportMaterialSchema = z.object({
    materialTypeId: z.string().uuid(),
    direction: z.enum(MATERIAL_FLOWS),
    /** Граммы. Никаких дробных: 12,3 кг — это 12300. */
    qtyG: z.number().int().min(0),
});
export const incidentSchema = z
    .object({
    kind: z.enum(INCIDENT_KINDS),
    durationMinutes: z.number().int().min(0).max(24 * 60).nullish(),
    comment: z.string().trim().max(1000).nullish(),
    /** Для обучения — кого обучали. */
    traineeUserId: z.string().uuid().nullish(),
})
    .superRefine((incident, ctx) => {
    if (incident.kind === 'REPAIR' && !incident.comment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['comment'],
            message: 'Опишите, что ремонтировали',
        });
    }
    if (incident.kind === 'TRAINING' && !incident.traineeUserId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['traineeUserId'],
            message: 'Укажите, кого обучали',
        });
    }
});
/**
 * Работы бригады, не связанные с выработкой агрегата.
 *
 * Бригада с гильотины палетирует готовую плитку, грузит и разгружает машины,
 * убирает цех. Палеты считаются двумя разными числами: запалетить и отгрузить —
 * это разная работа, и делают их в разном объёме.
 *
 * Со склада эти числа ничего не списывают. Отгрузку оформляет отдельный
 * документ с покупателем и накладной, иначе продукция ушла бы дважды.
 */
export const crewWorkSchema = z.object({
    palletsPacked: z.number().int().min(0).max(10_000).default(0),
    palletsLoaded: z.number().int().min(0).max(10_000).default(0),
    tasks: z.array(z.enum(CREW_TASKS)).default([]),
});
/** Ссылка на снимок: файл приезжает своей очередью, здесь только назначение. */
export const photoRefSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(PHOTO_KINDS),
});
export const shiftReportPayloadSchema = z
    .object({
    reportId: z.string().uuid(),
    siteId: z.string().uuid(),
    machineId: z.string().uuid(),
    /** Дата НАЧАЛА смены, «ГГГГ-ММ-ДД». */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД'),
    shiftNumber: z.number().int().min(1).max(3),
    participants: z.array(participantSchema).min(1).max(4),
    /** Прессование: выработка по типоразмерам. */
    outputs: z.array(reportOutputSchema).default([]),
    /** Агломерация, дробление, смешивание: что переработали и что получили. */
    materials: z.array(reportMaterialSchema).default([]),
    /** Смешивание: количество замесов. */
    batchesCount: z.number().int().min(0).nullish(),
    recipeId: z.string().uuid().nullish(),
    /** Резка и дробление: работа бригады помимо агрегата. */
    crewWork: crewWorkSchema.nullish(),
    incidents: z.array(incidentSchema).default([]),
    comment: z.string().trim().max(2000).nullish(),
    /** Снимки: сами файлы приезжают отдельной очередью, здесь — назначение. */
    photos: z.array(photoRefSchema).default([]),
    /** Только для корректировки: какой отчёт исправляем. */
    correctsReportId: z.string().uuid().nullish(),
    /** Администратор сдаёт за оператора — почему. */
    onBehalfReason: z.string().trim().min(3).max(500).nullish(),
})
    .superRefine((payload, ctx) => {
    const shares = payload.participants.reduce((sum, participant) => sum + participant.shareBp, 0);
    if (shares !== BASIS_POINTS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['participants'],
            message: `Доли участия должны давать в сумме 100%, а дают ${shares / 100}%`,
        });
    }
    const ids = payload.participants.map((participant) => participant.userId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['participants'],
            message: 'Один и тот же сотрудник указан дважды',
        });
    }
    // Ноль — допустимое значение, но его нужно объяснить
    const nothingProduced = payload.outputs.every((output) => output.qtyPieces === 0) &&
        payload.materials
            .filter((material) => material.direction === 'PRODUCED')
            .every((material) => material.qtyG === 0) &&
        (payload.batchesCount ?? 0) === 0;
    if (nothingProduced && !payload.comment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['comment'],
            message: 'Выработка нулевая — напишите, почему',
        });
    }
    // Оба снимка обязательны: один показывает, что сделали, второй — в каком
    // виде оставили рабочее место. Требуется сделанный снимок, а не доехавший
    // до сервера: в цеху связь пропадает, а терять из-за этого цифры смены
    // нельзя — фотографии уходят своей очередью.
    const kinds = new Set(payload.photos.map((photo) => photo.kind));
    for (const required of REPORT_PHOTO_KINDS) {
        if (!kinds.has(required)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['photos'],
                message: required === 'PRODUCT'
                    ? 'Сфотографируйте произведённую продукцию'
                    : 'Сфотографируйте переданное рабочее место',
            });
        }
    }
    const photoIds = payload.photos.map((photo) => photo.id);
    if (new Set(photoIds).size !== photoIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['photos'],
            message: 'Один и тот же снимок приложен дважды',
        });
    }
});
// ───────────────────────────── расчёты ─────────────────────────────
/**
 * Нормативный расход замеса на выработку прессования.
 * Прессовщик расход не вводит — он списывается по норме от числа плит.
 */
export function mixConsumptionG(outputs, norms, reworkLossBp = 0) {
    return outputs.reduce((sum, output) => {
        const norm = norms.get(output.productSizeId) ?? 0;
        // Годная плита забирает норму целиком
        const good = output.qtyPieces * norm;
        // Брак переплавляют тут же, и материал почти весь возвращается. Списывается
        // только потерянная при переплавке доля — иначе склад ушёл бы в минус
        // на том, что физически осталось в цехе.
        const defectLoss = Math.round(((output.defectPieces ?? 0) * norm * reworkLossBp) / BASIS_POINTS);
        return sum + good + defectLoss;
    }, 0);
}
/** Сколько сырья пришлось на один замес. null — если замесов не было. */
export function perBatchG(totalG, batchesCount) {
    if (batchesCount <= 0)
        return null;
    return Math.round(totalG / batchesCount);
}
/** Отклонение от рецептуры. Соблюдение необязательно — это только подсветка. */
export function deviationFromRange(value, minG, maxG) {
    if (value < minG)
        return 'below';
    if (value > maxG)
        return 'above';
    return 'ok';
}
/**
 * Отклонение выхода от нормы участка, в базисных пунктах.
 * Отрицательное значение — вышло меньше нормы.
 */
export function yieldDeviationBp(actualBp, normBp) {
    return actualBp - normBp;
}
/** Заметным считаем отклонение больше пяти процентных пунктов. */
export const YIELD_ALERT_BP = 500;
export function isYieldAlarming(actualBp, normBp) {
    return Math.abs(yieldDeviationBp(actualBp, normBp)) > YIELD_ALERT_BP;
}
/**
 * Приёмка сменного отчёта мастером.
 *
 * Оператор сдаёт цифры, но смена считается закрытой только после того, как
 * мастер их подтвердил. Так ошибка находится в тот же день, а не через месяц
 * при разборе расхождений.
 */
export const reportAcceptSchema = z.object({
    reportId: z.string().uuid(),
    comment: z.string().trim().max(1000).nullish(),
});
//# sourceMappingURL=reports.js.map